import { create } from 'zustand';
import { Product } from '../../products/types/product.type';
import { Basket } from '../../baskets/types/basket.type';
import { OrderItem } from '../types/order-item.type';
import { formatProductToOrderItem, formatBasketToOrderItem } from '../utils/format-catalog-to-order-item.util';

type VirtualToAdd = Omit<OrderItem, 'productId' | 'photo'>;

type OrderFormStore = {
  items: OrderItem[];
  initialize: (items: OrderItem[]) => void;
  addProduct: (product: Product) => void;
  addBasket: (basket: Basket) => void;
  addVirtual: (item: VirtualToAdd) => void;
  increaseQuantity: (id: string) => void;
  decreaseQuantity: (id: string) => void;
  changeQuantity: (id: string, value: string) => void;
  reset: () => void;
};

const initialState: { items: OrderItem[] } = { items: [] };

export const useOrderFormStore = create<OrderFormStore>((set) => ({
  ...initialState,

  initialize: (items) => set({ items }),

  addProduct: (product) =>
    set((state) => {
      const existing = state.items.find((item) => item.productId === product.id);
      if (existing) {
        return {
          items: state.items.map((item) =>
            item.productId === product.id ? { ...item, quantity: item.quantity + 1 } : item
          ),
        };
      }
      return { items: [...state.items, formatProductToOrderItem(product)] };
    }),

  addBasket: (basket) =>
    set((state) => {
      const existing = state.items.find((item) => item.productId === basket.id);
      if (existing) {
        return {
          items: state.items.map((item) =>
            item.productId === basket.id ? { ...item, quantity: item.quantity + 1 } : item
          ),
        };
      }
      return { items: [...state.items, formatBasketToOrderItem(basket)] };
    }),

  addVirtual: (item) =>
    set((state) => ({
      items: [
        ...state.items,
        { ...item, productId: `virtual-${Math.random().toString(36).slice(2, 9)}` },
      ],
    })),

  increaseQuantity: (id) =>
    set((state) => {
      const hasItem = state.items.some((item) => item.productId === id);
      if (!hasItem) return state;
      return {
        items: state.items.map((item) =>
          item.productId === id ? { ...item, quantity: item.quantity + 1 } : item
        ),
      };
    }),

  decreaseQuantity: (id) =>
    set((state) => {
      const hasItem = state.items.some((item) => item.productId === id);
      if (!hasItem) return state;
      return {
        items: state.items
          .map((item) => (item.productId === id ? { ...item, quantity: item.quantity - 1 } : item))
          .filter((item) => item.productId !== id || item.quantity > 0),
      };
    }),

  changeQuantity: (id, value) =>
    set((state) => {
      const newQuantity = parseInt(value, 10);
      const hasItem = state.items.some((item) => item.productId === id);
      if (!hasItem) return state;
      const updateOrRemove = (item: OrderItem): OrderItem | null => {
        if (item.productId !== id) return item;
        if (Number.isNaN(newQuantity) || newQuantity <= 0) return null;
        return { ...item, quantity: newQuantity };
      };
      return {
        items: state.items
          .map(updateOrRemove)
          .filter((item): item is OrderItem => item !== null),
      };
    }),

  reset: () => set(initialState),
}));
